import React, { useEffect, useRef, useState } from 'react'
import { usePageViewItem } from 'shared/components/engagement/usePageViewItem'
import { isSurfaceOpen, setSurfaceOpen, useSurfaceOpen } from '../engagementSurfaces'
import AnnouncementView from './AnnouncementView'
import type { Announcement } from './announcementQueue'
import { isPageViewCandidate, openCta, pickAutoShow, readSessionShown, writeSessionShown } from './announcementQueue'
import { useAnnouncements } from './useAnnouncements'

/**
 * Opens announcements by itself, in their display mode (#25346). Behind `announcementCenter`;
 * renders nothing during BugBug runs. The rest wait in the header's history list
 * (AnnouncementHistoryButton).
 *
 * - Startup: the newest unread one, once per tab session.
 * - Page-view (#25610): an unread one whose path pattern matches the current page, when no other
 *   announcement or survey is open. Not limited to one per session; `viewedAt` makes it once per user.
 */
export default function AnnouncementCenter() {
  const { enabled, announcements, mark } = useAnnouncements()
  // Pinned, so the open announcement stays put when the cache updates it (viewedAt) or drops it.
  const [current, setCurrent] = useState<Announcement | null>(null)
  // The tab-level guard: a reload in this tab shows nothing more, even when sessionStorage throws.
  const shownRef = useRef(readSessionShown())
  // Page-view announcements opened in this tab, in case the viewed write fails.
  const pageShownRef = useRef(new Set<string>())
  const surveyOpen = useSurfaceOpen('survey')
  const pageViewNext = usePageViewItem(enabled ? announcements : [], (a) =>
    isPageViewCandidate(a, pageShownRef.current)
  )

  const open = (next: Announcement) => {
    setSurfaceOpen('announcement', true)
    setCurrent(next)
    // Viewed on display, not on close: a user who reloads mid-read has still seen it, and the
    // set-once timestamp keeps it from opening again in any tab.
    mark(next, 'viewed', 'auto')
  }

  useEffect(() => {
    // A page-view one may be open; this one then opens after it closes.
    if (!enabled || isSurfaceOpen('announcement')) return
    const next = pickAutoShow(announcements, shownRef.current)
    if (!next) return
    shownRef.current = true
    writeSessionShown(next.id)
    open(next)
    // `open` only calls `mark` (listed) and stable setters. `current` is listed so a startup
    // announcement held back by an open page-view one opens once that closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, announcements, mark, current])

  useEffect(() => {
    // Read the store, not only the state: the startup effect above or SurveyCenter may have opened
    // a dialog earlier in this same commit, and `current`/`surveyOpen` do not show it yet.
    if (!enabled || !pageViewNext || current || surveyOpen) return
    if (isSurfaceOpen('announcement') || isSurfaceOpen('survey')) return
    pageShownRef.current.add(pageViewNext.id)
    open(pageViewNext)
    // `open` only calls `mark` (listed) and stable setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pageViewNext, current, surveyOpen, mark])

  useEffect(() => () => setSurfaceOpen('announcement', false), [])

  if (!enabled || !current) return null

  const close = () => {
    setSurfaceOpen('announcement', false)
    setCurrent(null)
  }

  return (
    <AnnouncementView
      announcement={current}
      mode={current.displayMode === 'popup' ? 'popup' : 'modal'}
      onClose={() => {
        mark(current, 'dismissed', 'auto')
        close()
      }}
      onCta={() => {
        mark(current, 'ctaClicked', 'auto')
        openCta(current)
        close()
      }}
    />
  )
}
